/* =========================================================
   The Circle — lists, takes and notes, backed by the VRO app.

   Everything here reads and writes through window.VroCircleApi
   (assets/vro-circle-api.js), which owns the App Proxy client, the login
   popup and the consent prompt. Nothing about a list lives in
   localStorage any more: lists, items, votes and notes are all server
   state, so they follow the customer across devices and are the same
   data the people they share with are looking at.

   One renderer, three entry points — they all draw the same markup into
   the same .cart-panel/.circle-item shell:
     • #circleMyListPanel   a list of yours, opened from the cart or profile
     • #circlePanel         the same, opened as a global slide-in
     • #circlePanelPage     the same again, always-open, on the Circle page
                            (which can also open a SHARED list by ?t=token)

   Product presentation (image, price, availability) still comes from
   Shopify live via /products/{handle}.js rather than the snapshot stored
   at add-time, so a price change or a sold-out variant shows correctly.
   The snapshot is the fallback when that fetch fails.
   ========================================================= */
(function () {
  'use strict';

  var Api = window.VroCircleApi;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------------- small helpers ---------------- */

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function toast(msg) {
    if (window.showToast) window.showToast(msg);
  }

  // A round spinner, not a line of text — used everywhere a panel is
  // waiting on the network. "Loading…" as plain text sat flush left with
  // nothing else on the screen; this reads as the app doing something.
  var SPINNER_HTML = '<div class="circle-loading"><span class="circle-spinner" aria-hidden="true"></span></div>';

  function money(paise) {
    if (paise == null) return '';
    return '₹' + (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  }

  /* Shopify CDN URLs usually arrive carrying ?v=..., but not always —
     blindly appending "&width=" builds a broken URL when they don't. */
  function withWidth(url, w) {
    if (!url) return '';
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function initialsOf(name) {
    if (!name) return '?';
    return name.trim().split(/\s+/).slice(0, 2).map(function (w) { return w[0]; }).join('').toUpperCase();
  }

  function timeAgo(iso) {
    var mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    if (mins < 1) return 'just now';
    if (mins < 60) return mins + 'm';
    if (mins < 1440) return Math.round(mins / 60) + 'h';
    return Math.round(mins / 1440) + 'd';
  }

  /* Who "you" are on screen. Liquid seeds this from the Shopify customer,
     which for an email sign-in is nobody at all — /circle/me then supplies
     the name the customer gave us, and setYou() keeps the initials derived
     from that same name rather than from a second source that can disagree
     with it. That disagreement is exactly what shipped: the owner's own
     note showed one set of initials on their screen and another on their
     friend's, for the same note. */
  var youName = window.__circleYouName || '';
  var youInitials = window.__circleYouInitials || '';

  function setYou(name) {
    youName = (name || '').trim();
    youInitials = youName ? initialsOf(youName) : '';
  }

  // Falls back only where there's genuinely nothing to show.
  function youAvatar() { return youInitials || 'ME'; }
  function yourListLabel() {
    return youName ? esc(youName) + '&rsquo;s list' : 'Your list';
  }

  /* Product JSON, fetched once per handle and reused — a list of six
     items re-rendering on every note post shouldn't refetch six products. */
  var productCache = {};   // handle -> promise
  var productReady = {};   // handle -> the resolved product, for a synchronous read
  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; })
      .then(function (p) { productReady[handle] = p || null; return p; });
    return productCache[handle];
  }

  /* What we already have, without waiting. Lets the first paint use a
     product fetched earlier in the session instead of re-deciding to wait
     for it. */
  function cachedProduct(handle) {
    return (handle && productReady[handle]) || null;
  }

  /* ---------------- list rendering ----------------
     `data` is the payload of GET /circle/lists/:id (owner's own list) or
     GET /circle/s/:token (a shared list). Both carry items/notes; only
     the owner's carries votes broken down per voter, so the vote pills
     render from whichever is present.

     Vote pills are counters here, never buttons: the owner can't vote on
     their own list (the backend refuses it), and a friend votes through
     the "Your take" flow, not this panel.
  ---------------------------------------------- */

  function itemVoteCounts(item) {
    return { up: Number(item.ups) || 0, down: Number(item.downs) || 0 };
  }

  function notesFor(data, itemId) {
    return (data.notes || []).filter(function (n) { return Number(n.list_item_id) === Number(itemId); });
  }

  function noteRowHTML(note) {
    var isYou = Number(note.author_id) === Number(data_me);
    var name = isYou ? 'You' : (note.display_name || 'Someone');
    // Edit is yours to do, once — the same one-edit rule the backend
    // enforces (PATCH /circle/notes/:id refuses a second one). Once
    // note.edited_at comes back set, the Edit button and its form just
    // don't render any more; "Edited" is the only trace left, like
    // WhatsApp leaves on a corrected message.
    var canEdit = isYou && !note.edited_at;
    var editHTML = canEdit
      ? '<button type="button" class="circle-note-edit-btn" data-circle-note-edit="' + note.id + '">Edit</button>' +
        '<div class="circle-note-edit-form" data-circle-note-edit-form="' + note.id + '">' +
          '<textarea maxlength="250">' + esc(note.body) + '</textarea>' +
          '<div class="circle-note-edit-actions">' +
            '<button type="button" class="circle-note-edit-cancel">Cancel</button>' +
            '<button type="button" class="circle-note-edit-save" data-circle-note-save="' + note.id + '">Save</button>' +
          '</div>' +
        '</div>'
      : '';
    return '<div class="circle-note' + (isYou ? ' circle-note--you' : '') + '" data-circle-note-id="' + note.id + '">' +
      '<span class="circle-avatar circle-note-avatar">' + esc(isYou ? youAvatar() : initialsOf(note.display_name)) + '</span>' +
      '<div class="circle-note-lines">' +
        '<p class="circle-note-author">' + esc(name) + ' &middot; ' + esc(timeAgo(note.created_at)) +
          (note.edited_at ? ' &middot; <span class="circle-note-edited">Edited</span>' : '') +
        '</p>' +
        '<p class="circle-note-text">' + esc(note.body) + '</p>' +
        editHTML +
      '</div>' +
    '</div>';
  }

  // set by loadMe() so noteRowHTML can tell "You" apart from everyone else
  var data_me = null;

  function itemHTML(item, product, data, opts) {
    opts = opts || {};
    var canAddNote = opts.canAddNote !== false;
    var canRemove = opts.canRemove !== false;
    var snap = item.snapshot || {};
    var notes = notesFor(data, item.id);
    var votes = itemVoteCounts(item);

    var title = (product && product.title) || snap.title || 'Product';
    var vendor = (product && product.vendor) || snap.vendor || '';
    var img = (product && (product.featured_image || (product.images && product.images[0]))) || snap.image || '';
    var price = product ? product.price : snap.price;
    var compareAt = product ? product.compare_at_price : snap.compare_at;

    var variant = null;
    if (product && product.variants && product.variants.length) {
      variant = product.variants.find(function (v) { return v && v.available; }) || product.variants[0];
    }
    var variantSuffix = variant && variant.title && variant.title !== 'Default Title'
      ? ' &middot; ' + esc(variant.title)
      : (snap.variant ? ' &middot; ' + esc(snap.variant) : '');

    var discountHTML = '';
    if (compareAt && price && compareAt > price) {
      discountHTML = '<span class="circle-sku-discount">' + Math.round((compareAt - price) * 100 / compareAt) + '% off</span>';
    }

    var takesHTML;
    if (!notes.length && !votes.up && !votes.down) {
      takesHTML =
        '<div class="circle-takes-row circle-takes-row--empty">' +
          '<span class="circle-takes-count" data-circle-notes-count="' + item.id + '" data-count="0">No takes yet</span>' +
        '</div>' +
        '<div data-circle-notes="' + item.id + '"></div>';
    } else {
      takesHTML =
        '<div class="circle-takes-row">' +
          '<span class="circle-takes-count" data-circle-notes-count="' + item.id + '" data-count="' + notes.length + '">' +
            notes.length + ' note' + (notes.length === 1 ? '' : 's') +
          '</span>' +
          '<div class="circle-votes">' +
            '<span class="circle-vote-pill' + (votes.up >= votes.down && votes.up ? ' is-leading' : '') + '">' +
              '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M7 22V11m0 11H4a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1h3m3 0V6a3 3 0 0 1 3-3l1 6h5.28a2 2 0 0 1 1.98 2.29l-1.14 8A2 2 0 0 1 17.14 21H10a3 3 0 0 1-3-3z"/></svg>' +
              '<span data-circle-vote-count>' + votes.up + '</span></span>' +
            '<span class="circle-vote-pill' + (votes.down > votes.up ? ' is-leading' : '') + '">' +
              '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M17 2v11m0-11h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1h-3m-3 0v5a3 3 0 0 1-3 3l-1-6H4.72a2 2 0 0 1-1.98-2.29l1.14-8A2 2 0 0 1 5.86 3H13a3 3 0 0 1 3 3z"/></svg>' +
              '<span data-circle-vote-count>' + votes.down + '</span></span>' +
          '</div>' +
        '</div>' +
        '<div data-circle-notes="' + item.id + '">' + notes.map(noteRowHTML).join('') + '</div>';
    }

    var addNoteHTML = canAddNote
      ? '<div class="circle-add-note-row">' +
          '<button type="button" class="circle-add-note-btn" data-circle-add-note="' + item.id + '">Add a note' +
            '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="circle-note-form" data-circle-note-form="' + item.id + '">' +
          '<textarea placeholder="Add your take…" maxlength="250"></textarea>' +
          '<div class="circle-note-form-actions">' +
            '<button type="button" class="circle-note-form-cancel">Cancel</button>' +
            '<button type="button" class="circle-note-form-post">Post</button>' +
          '</div>' +
        '</div>'
      : '';

    return '<div class="circle-item" data-circle-item="' + item.id + '">' +
      '<div class="circle-sku-card">' +
        '<div class="circle-sku-image-wrap">' +
          (img ? '<img src="' + esc(withWidth(img, 800)) + '" alt="' + esc(title) + '" class="circle-sku-image" loading="lazy">' : '') +
          // Taking an item off the list is the owner's call, not a visitor's.
          (canRemove
            ? '<button type="button" class="circle-sku-remove" data-circle-remove-item="' + item.id + '" aria-label="Remove from list">' +
                '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/></svg>' +
              '</button>'
            : '') +
          (variant
            ? '<button type="button" class="circle-sku-add" data-add-variant="' + variant.id + '"' +
              (variant.available ? '' : ' disabled') + ' aria-label="Add to bag">' +
              '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
              '</button>'
            : '') +
        '</div>' +
        '<div class="circle-sku-data">' +
          '<p class="circle-sku-brand">' + esc(vendor) + '</p>' +
          '<p class="circle-sku-name">' + esc(title) + variantSuffix + '</p>' +
          '<p class="circle-sku-price"><span class="circle-sku-price-current">' + esc(money(price)) + '</span>' + discountHTML + '</p>' +
        '</div>' +
      '</div>' +
      '<div class="circle-takes">' + takesHTML + addNoteHTML + '</div>' +
    '</div>';
  }

  /* How far along the asking is, in one line — the same sentence in the
     list footer and in the settings sheet, so they can't disagree. */
  function answeredLabel(data) {
    var share = (data && data.share) || null;
    if (!share || !share.token) return 'Private until you ask';
    var voted = Number(share.voted) || 0;
    var invited = Number(share.invited) || 0;
    return invited ? voted + ' of ' + invited + ' have answered'
                   : voted + (voted === 1 ? ' take' : ' takes') + ' so far';
  }

  /**
   * Draws a whole list into one of the panel shells.
   * opts.canAddNote — false for a view-only share link.
   * opts.isOwner    — owner gets the share/invite affordances and footer.
   */
  function renderListInto(panel, data, opts) {
    opts = opts || {};
    var body = panel.querySelector('.cart-panel-body');
    var footer = panel.querySelector('.cart-panel-footer');
    var titleEl = panel.querySelector('.cart-panel-title');
    var list = data.list || {};
    var listName = list.name || data.list_name || 'List';

    panel.setAttribute('data-list-id', list.id || data.share_id || '');
    if (titleEl) titleEl.textContent = listName;
    if (!body) return;

    var shared = opts.isOwner ? Boolean(data.share && data.share.token) : true;
    var memberChips = (data.members || []).map(function (m) {
      return '<span class="circle-avatar" title="' + esc(m.display_name || 'Someone') + '">' +
        esc(initialsOf(m.display_name)) + '</span>';
    }).join('');

    var pillHTML;
    if (!opts.isOwner) {
      pillHTML = '<span class="circle-owner-pill"><span class="circle-owner-dot"></span>' +
        esc(data.owner_name || 'Their') + '&rsquo;s list</span>';
    } else if (shared) {
      pillHTML = '<span class="circle-owner-pill"><span class="circle-owner-dot"></span>' + yourListLabel() + '</span>' +
        '<div class="circle-avatars">' + memberChips +
          '<button type="button" class="circle-invite-btn" data-circle-mylist-ask aria-label="Ask your Circle">' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
          '</button></div>';
    } else {
      pillHTML = '<span class="circle-owner-pill circle-owner-pill--private"><span class="circle-owner-dot"></span>Private</span>' +
        '<button type="button" class="circle-invite-btn circle-share-btn" data-circle-mylist-ask aria-label="Share this list">' +
          '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="10.6" x2="15.4" y2="6.4"/><line x1="8.6" y1="13.4" x2="15.4" y2="17.6"/></svg>' +
        '</button>';
    }

    body.innerHTML = '<div class="circle-row">' + pillHTML + '</div><div data-circle-items></div>';
    var itemsWrap = body.querySelector('[data-circle-items]');
    var items = data.items || [];

    var itemOpts = {
      canAddNote: opts.canAddNote !== false,
      canRemove: opts.isOwner === true
    };

    if (!items.length) {
      itemsWrap.innerHTML = '<p class="circle-mylist-empty">No items in this list yet.</p>';
    } else {
      /* Paint from the snapshot taken when the item was added — it arrived
         with the list, so the list appears as soon as the API answers
         instead of after a product fetch per item has finished. Live
         product data (current price, availability, the variant to add to
         the bag) swaps in per item as it lands.

         The whole list used to wait on the slowest of those fetches, which
         on a six-item list is what made opening one feel slow. */
      var handles = items.map(function (it) { return (it.snapshot || {}).handle; });
      itemsWrap.innerHTML = items.map(function (it, i) {
        return itemHTML(it, cachedProduct(handles[i]), data, itemOpts);
      }).join('');

      items.forEach(function (it, i) {
        if (cachedProduct(handles[i])) return;   // already drawn with it
        fetchProduct(handles[i]).then(function (product) {
          if (!product) return;
          var node = itemsWrap.querySelector('[data-circle-item="' + it.id + '"]');
          // Gone if the panel re-rendered while this was in flight.
          if (!node || !node.isConnected) return;
          // Leave a note the customer is in the middle of writing alone.
          if (node.querySelector('.circle-note-form.is-open')) return;
          node.outerHTML = itemHTML(it, product, data, itemOpts);
        });
      });
    }

    // Rename and delete are the owner's to do, so the 3-dots is theirs too.
    var moreBtn = panel.querySelector('[data-circle-list-settings]');
    if (moreBtn) moreBtn.hidden = !opts.isOwner;

    if (!footer) return;
    if (opts.isOwner && shared) {
      var answered = answeredLabel(data);
      footer.innerHTML =
        '<div class="circle-notify-row">' +
          '<div class="circle-notify-text">' +
            '<p class="circle-notify-title">Ping me when a take lands</p>' +
            '<p class="circle-notify-sub">' + esc(answered) + '</p>' +
          '</div>' +
          '<button type="button" class="circle-toggle' + (list.notify_on_vote ? ' is-on' : '') + '" role="switch" ' +
            'aria-checked="' + (list.notify_on_vote ? 'true' : 'false') + '" data-circle-notify-toggle>' +
            '<span class="circle-toggle-knob"></span></button>' +
        '</div>' +
        '<a href="/cart" class="circle-goto-cart-btn">Go to cart</a>';
    } else if (opts.isOwner) {
      footer.innerHTML =
        '<button type="button" class="circle-goto-cart-btn circle-goto-cart-btn--dark" data-circle-mylist-ask>Ask your Circle</button>' +
        '<p class="circle-private-caption">Private until you ask</p>';
    } else {
      footer.innerHTML = '<a href="/cart" class="circle-goto-cart-btn">Go to cart</a>';
    }
  }

  ready(function () {
    if (!Api) return;   // vro-circle-api.js must load first

    /* ---------------- who am I ---------------- */
    /* Asked once and remembered. Several panels want to know who "you" are
       before they can label a note, and each of them asking separately was
       a round trip through the App Proxy for an answer that doesn't change
       while the page is open. `fresh` forces a re-ask after the name is
       set for the first time. */
    var mePromise = null;
    function loadMe(fresh) {
      if (Api.isGuest()) return Promise.resolve(null);
      if (mePromise && !fresh) return mePromise;
      mePromise = Api.api('/me').then(function (me) {
        if (me && me.display_name) setYou(me.display_name);
        data_me = me && me.customer_id != null ? me.customer_id : data_me;
        return me;
      }).catch(function () {
        mePromise = null;   // a failed lookup shouldn't be cached as the answer
        return null;
      });
      return mePromise;
    }

    /* Signing in with an email doesn't give Shopify a name to pass on, so
       whatever this customer says next would show up on their Circle's
       screens as "Someone". Asked once, the first time they do something
       their Circle will see; saved, and never asked again. */
    function ensureName() {
      return loadMe().then(function (me) {
        if (!me || me.display_name) return me;
        return askName().then(function (name) {
          if (!name) return me;
          return Api.api('/me', { method: 'PUT', body: { display_name: name } })
            .then(function (out) {
              setYou(out.display_name);
              mePromise = Promise.resolve(out);   // the cached answer is now out of date
              return out;
            })
            .catch(function () { return me; });
        });
      });
    }

    /* One reusable "type one thing" dialog — the name we don't have yet,
       and the new name for a list. Same shell, different words. Resolves
       with the text, or null if they backed out. */
    var nameEls = null;
    var pendingName = null;

    function promptText(opts) {
      opts = opts || {};
      if (!nameEls) {
        var backdrop = document.createElement('div');
        backdrop.className = 'circle-modal-backdrop';
        backdrop.hidden = true;

        var modal = document.createElement('div');
        modal.className = 'circle-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.innerHTML =
          '<div class="circle-modal-head"><h2 class="circle-modal-title" data-circle-name-title></h2></div>' +
          '<input type="text" class="circle-modal-input" maxlength="60" autocomplete="off" data-circle-name-input>' +
          '<div class="circle-modal-input-meta">' +
            '<span class="circle-modal-input-hint" data-circle-name-hint></span>' +
          '</div>' +
          '<div class="circle-modal-actions">' +
            '<button type="button" class="circle-modal-cancel" data-circle-name-skip></button>' +
            '<button type="button" class="circle-modal-create-btn" data-circle-name-save></button>' +
          '</div>';

        document.body.appendChild(backdrop);
        document.body.appendChild(modal);
        nameEls = {
          backdrop: backdrop,
          modal: modal,
          title: modal.querySelector('[data-circle-name-title]'),
          input: modal.querySelector('[data-circle-name-input]'),
          hint: modal.querySelector('[data-circle-name-hint]'),
          cancel: modal.querySelector('[data-circle-name-skip]'),
          save: modal.querySelector('[data-circle-name-save]')
        };
      }

      nameEls.title.textContent = opts.title || '';
      nameEls.hint.textContent = opts.hint || '';
      nameEls.cancel.textContent = opts.cancel || 'Cancel';
      nameEls.save.textContent = opts.save || 'Save';
      nameEls.input.placeholder = opts.placeholder || '';
      nameEls.input.setAttribute('maxlength', String(opts.maxlength || 60));
      nameEls.input.setAttribute('autocomplete', opts.autocomplete || 'off');
      nameEls.input.value = opts.value || '';

      return new Promise(function (resolve) {
        pendingName = resolve;
        nameEls.backdrop.hidden = false;
        requestAnimationFrame(function () {
          nameEls.backdrop.classList.add('is-visible');
          nameEls.modal.classList.add('is-open');
        });
        document.body.style.overflow = 'hidden';
        setTimeout(function () { nameEls.input.focus(); nameEls.input.select(); }, 300);
      });
    }

    function askName() {
      return promptText({
        title: 'What should we call you?',
        placeholder: 'Your name',
        hint: 'Your Circle sees this next to your takes',
        cancel: 'Skip',
        autocomplete: 'name',
        maxlength: 60
      });
    }

    function closeName(value) {
      if (!nameEls) return;
      nameEls.backdrop.classList.remove('is-visible');
      nameEls.modal.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(function () { nameEls.backdrop.hidden = true; }, 320);
      var resolve = pendingName;
      pendingName = null;
      if (resolve) resolve(value || null);
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-circle-name-save]')) {
        closeName(nameEls && nameEls.input.value.trim());
        return;
      }
      if (e.target.closest('[data-circle-name-skip]') || (nameEls && e.target === nameEls.backdrop)) {
        closeName(null);
      }
    });

    /* Confirm, for the one action that can't be taken back. */
    var confirmEls = null;
    var pendingConfirm = null;

    function askConfirm(opts) {
      opts = opts || {};
      if (!confirmEls) {
        var cb = document.createElement('div');
        cb.className = 'circle-modal-backdrop';
        cb.hidden = true;

        var cm = document.createElement('div');
        cm.className = 'circle-modal';
        cm.setAttribute('role', 'dialog');
        cm.setAttribute('aria-modal', 'true');
        cm.innerHTML =
          '<div class="circle-modal-head"><h2 class="circle-modal-title" data-circle-confirm-title></h2></div>' +
          '<p class="circle-modal-consent-copy" data-circle-confirm-copy></p>' +
          '<div class="circle-modal-actions">' +
            '<button type="button" class="circle-modal-cancel" data-circle-confirm-no>Cancel</button>' +
            '<button type="button" class="circle-modal-create-btn circle-modal-create-btn--danger" data-circle-confirm-yes></button>' +
          '</div>';

        document.body.appendChild(cb);
        document.body.appendChild(cm);
        confirmEls = {
          backdrop: cb,
          modal: cm,
          title: cm.querySelector('[data-circle-confirm-title]'),
          copy: cm.querySelector('[data-circle-confirm-copy]'),
          yes: cm.querySelector('[data-circle-confirm-yes]')
        };
      }

      confirmEls.title.textContent = opts.title || 'Are you sure?';
      confirmEls.copy.textContent = opts.copy || '';
      confirmEls.yes.textContent = opts.confirm || 'Delete';

      return new Promise(function (resolve) {
        pendingConfirm = resolve;
        confirmEls.backdrop.hidden = false;
        requestAnimationFrame(function () {
          confirmEls.backdrop.classList.add('is-visible');
          confirmEls.modal.classList.add('is-open');
        });
        document.body.style.overflow = 'hidden';
      });
    }

    function closeConfirm(answer) {
      if (!confirmEls) return;
      confirmEls.backdrop.classList.remove('is-visible');
      confirmEls.modal.classList.remove('is-open');
      document.body.style.overflow = '';
      setTimeout(function () { confirmEls.backdrop.hidden = true; }, 320);
      var resolve = pendingConfirm;
      pendingConfirm = null;
      if (resolve) resolve(Boolean(answer));
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-circle-confirm-yes]')) { closeConfirm(true); return; }
      if (e.target.closest('[data-circle-confirm-no]') || (confirmEls && e.target === confirmEls.backdrop)) {
        closeConfirm(false);
      }
    });

    /* ---------------- panels ---------------- */
    var backdrop = document.getElementById('circlePanelBackdrop');
    var globalPanel = document.getElementById('circlePanel');
    var pagePanel = document.getElementById('circlePanelPage');
    var myListBackdrop = document.getElementById('circleMyListBackdrop');
    var myListPanel = document.getElementById('circleMyListPanel');
    var currentListId = null;

    function showPanel(panel, panelBackdrop) {
      if (panelBackdrop) {
        panelBackdrop.hidden = false;
        requestAnimationFrame(function () { panelBackdrop.classList.add('is-visible'); });
      }
      requestAnimationFrame(function () { panel.classList.add('is-open'); });
      panel.removeAttribute('inert');
      panel.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function hidePanel(panel, panelBackdrop) {
      if (panelBackdrop) panelBackdrop.classList.remove('is-visible');
      panel.classList.remove('is-open');
      panel.setAttribute('inert', '');
      panel.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (panelBackdrop) setTimeout(function () { panelBackdrop.hidden = true; }, 320);
    }

    /* ---------------- opening a list ---------------- */

    function openList(listId, panel, panelBackdrop) {
      panel = panel || myListPanel;
      panelBackdrop = panelBackdrop === undefined ? myListBackdrop : panelBackdrop;
      if (!panel) return Promise.resolve();
      currentListId = listId;

      showPanel(panel, panelBackdrop);
      panel.removeAttribute('data-share-token');   // this is your own list, not a link
      var body = panel.querySelector('.cart-panel-body');
      if (body) body.innerHTML = SPINNER_HTML;

      /* Both requests go out together. The members list is only needed for
         the avatar row, and waiting for it before even asking for the list
         cost a whole extra round trip through the App Proxy on every open. */
      return Promise.all([
        Api.call(function () { return Api.api('/lists/' + listId); }),
        Api.api('/members').catch(function () { return { members: [] }; })
      ])
        .then(function (both) {
          var data = both[0];
          data.members = (both[1] && both[1].members) || [];
          openList.last = data;   // the settings sheet reads its subtitle from here
          renderListInto(panel, data, { isOwner: true, canAddNote: true });
          // mark takes as seen — clears the "N new" badge
          Api.api('/lists/' + listId + '/seen', { method: 'POST', body: {} }).catch(function () {});
        })
        .catch(function (err) {
          if (body) {
            body.innerHTML = '<p class="circle-mylist-empty">' +
              (err.code === 'not_found' ? 'That list is gone.' : "Couldn't load this list.") + '</p>';
          }
        });
    }

    /* ---------------- list settings (the 3-dots) ----------------
       Everything the owner can do to a list as a whole, in one place:
       ask their Circle, rename it, or delete it. The three rows the
       header's 3-dots has always implied. */

    var settingsEls = null;
    var settingsListId = null;

    var SETTINGS_ICONS = {
      ask: '<path d="M12 3v13"/><path d="m8 7 4-4 4 4"/><path d="M4 15v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4"/>',
      rename: '<path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/>',
      remove: '<path d="M3 6h18"/><path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2"/>' +
              '<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M10 11v6"/><path d="M14 11v6"/>'
    };

    function settingsIcon(name) {
      return '<svg class="circle-settings-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" ' +
        'stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        SETTINGS_ICONS[name] + '</svg>';
    }

    var CHEVRON =
      '<svg class="circle-settings-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="m9 18 6-6-6-6"/></svg>';

    function buildSettings() {
      if (settingsEls) return settingsEls;

      var backdrop = document.createElement('div');
      backdrop.className = 'circle-modal-backdrop';
      backdrop.hidden = true;

      var modal = document.createElement('div');
      modal.className = 'circle-modal circle-modal--settings';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', 'List settings');
      modal.innerHTML =
        '<div class="circle-settings-head">' +
          '<h2 class="circle-settings-title">List settings</h2>' +
          '<button type="button" class="circle-settings-close" data-circle-settings-close aria-label="Close">' +
            '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="circle-settings-rows">' +
          '<button type="button" class="circle-settings-row" data-circle-settings-ask>' +
            settingsIcon('ask') +
            '<span class="circle-settings-text">' +
              '<span class="circle-settings-label">Ask your Circle</span>' +
              '<span class="circle-settings-sub" data-circle-settings-answered></span>' +
            '</span>' + CHEVRON +
          '</button>' +
          '<button type="button" class="circle-settings-row" data-circle-settings-rename>' +
            settingsIcon('rename') +
            '<span class="circle-settings-text"><span class="circle-settings-label">Rename list</span></span>' +
            CHEVRON +
          '</button>' +
          '<button type="button" class="circle-settings-row circle-settings-row--danger" data-circle-settings-delete>' +
            settingsIcon('remove') +
            '<span class="circle-settings-text"><span class="circle-settings-label">Delete list</span></span>' +
          '</button>' +
        '</div>';

      document.body.appendChild(backdrop);
      document.body.appendChild(modal);
      settingsEls = { backdrop: backdrop, modal: modal };
      return settingsEls;
    }

    function openListSettings(listId) {
      var els = buildSettings();
      settingsListId = listId;

      var data = openList.last && String((openList.last.list || {}).id) === String(listId)
        ? openList.last : null;
      els.modal.querySelector('[data-circle-settings-answered]').textContent = answeredLabel(data);

      els.backdrop.hidden = false;
      requestAnimationFrame(function () {
        els.backdrop.classList.add('is-visible');
        els.modal.classList.add('is-open');
      });
      document.body.style.overflow = 'hidden';
    }

    /* The slide-in list panel underneath stays open and keeps its own
       scroll lock, so this only clears body overflow when no such drawer is
       open — i.e. on the Circle page itself, where the list is the page and
       leaving the lock on froze the page after closing settings. */
    function closeListSettings() {
      if (!settingsEls) return;
      settingsEls.backdrop.classList.remove('is-visible');
      settingsEls.modal.classList.remove('is-open');
      if (!document.querySelector('.cart-panel.is-open:not(.circle-panel--static)')) {
        document.body.style.overflow = '';
      }
      setTimeout(function () { settingsEls.backdrop.hidden = true; }, 320);
    }

    function renameList(listId) {
      var current = openList.last && String((openList.last.list || {}).id) === String(listId)
        ? (openList.last.list.name || '') : '';

      return promptText({
        title: 'Rename list',
        value: current,
        placeholder: 'List name',
        hint: 'Your Circle sees this name',
        maxlength: 50
      }).then(function (name) {
        if (!name || name === current) return;
        return Api.call(function (extra) {
          return Api.api('/lists/' + listId, {
            method: 'PATCH',
            body: Object.assign({ name: name }, extra)
          });
        }).then(function () {
          if (openList.last && openList.last.list) openList.last.list.name = name;
          var openPanel = document.querySelector('.circle-panel[data-list-id="' + listId + '"], .cart-panel[data-list-id="' + listId + '"]');
          var title = openPanel && openPanel.querySelector('.cart-panel-title');
          if (title) title.textContent = name;
          renderYourLists();
          toast('Renamed');
        }).catch(function (err) {
          if (err.code === 'consent_declined' || err.code === 'login_required') return;
          toast("Couldn't rename that list.");
        });
      });
    }

    function deleteList(listId) {
      var name = openList.last && String((openList.last.list || {}).id) === String(listId)
        ? (openList.last.list.name || 'this list') : 'this list';

      return askConfirm({
        title: 'Delete ' + name + '?',
        copy: 'The list goes, and so do the takes and notes your Circle left on it. ' +
              'Anyone holding the link will find it closed. This can’t be undone.',
        confirm: 'Delete'
      }).then(function (yes) {
        if (!yes) return;
        return Api.call(function () {
          return Api.api('/lists/' + listId, { method: 'DELETE' });
        }).then(function () {
          openList.last = null;
          if (myListPanel) hidePanel(myListPanel, myListBackdrop);
          if (globalPanel) hidePanel(globalPanel, backdrop);
          /* The Circle page's panel is always open — there's nothing to
             close it to. Whatever else the owner has goes back on offer
             (the switcher if more than one remains, straight in if just
             one) instead of a dead end saying only what's gone. */
          if (pagePanel && String(pagePanel.getAttribute('data-list-id')) === String(listId)) {
            loadPageLists();
          }
          renderYourLists();
          toast('List deleted');
        }).catch(function (err) {
          if (err.code === 'login_required') return;
          toast("Couldn't delete that list.");
        });
      });
    }

    function openSharedList(token, panel) {
      if (!panel) return Promise.resolve();
      var body = panel.querySelector('.cart-panel-body');
      if (body) body.innerHTML = SPINNER_HTML;

      return Api.api('/s/' + encodeURIComponent(token))
        .then(function (data) {
          data.items = data.items || [];
          // Where a note posted from this panel has to go — it's a share,
          // so it can't use the owner-only /lists/:id/notes route.
          panel.setAttribute('data-share-token', token);
          /* A friend gets to keep talking, not just leave one line. Their
             note used to ride along with their vote, which meant one per
             item, edited by re-voting — while the owner could reply as
             often as they liked. */
          renderListInto(panel, data, {
            isOwner: false,
            canAddNote: data.mode === 'vote' && !Api.isGuest()
          });
        })
        .catch(function (err) {
          if (!body) return;
          var msg = "Couldn't load this list.";
          if (err.status === 410) msg = 'This link is closed.';
          if (err.status === 404) msg = "This link doesn't point at anything.";
          if (err.status === 401) msg = 'Sign in to see this list.';
          body.innerHTML = '<p class="circle-mylist-empty">' + msg + '</p>';
        });
    }

    window.VroCircleMyList = {
      open: function (id) { return openList(id); },
      close: function () { if (myListPanel) hidePanel(myListPanel, myListBackdrop); }
    };

    /* Lets the "Your take" overlay refresh the list sitting behind it on
       the Circle-vote page, so closing it shows the takes just given
       instead of the state the page loaded with. */
    window.VroCircleSharedList = {
      reload: function (token) {
        if (pagePanel) return openSharedList(token, pagePanel);
      }
    };

    /* The global slide-in panel shows your most recent list; the
       always-open page instance can also be pointed at a specific list
       (?list=id) or a shared one (?t=token). */
    if (backdrop && globalPanel) {
      window.VroCirclePanel = {
        open: function () {
          showPanel(globalPanel, backdrop);
          var body = globalPanel.querySelector('.cart-panel-body');
          if (body) body.innerHTML = SPINNER_HTML;
          Api.call(function () { return Api.api('/lists'); })
            .then(function (d) {
              var first = (d.lists || [])[0];
              if (!first) {
                var body = globalPanel.querySelector('.cart-panel-body');
                if (body) body.innerHTML = '<p class="circle-mylist-empty">No lists yet.</p>';
                return;
              }
              return openList(first.id, globalPanel, null);
            })
            .catch(function () {});
        },
        close: function () { hidePanel(globalPanel, backdrop); }
      };
    }

    // Behind loadMe() because the renderer needs to know who "you" are to
    // label your own notes — the page panel draws itself immediately, so
    // without this it would race and show your notes under your name
    // instead of "You".
    var meReady = loadMe();

    /* ---------------- Circle page: which list, when there's more than one ----------------
       A single list opens straight into itself — nothing to choose. With
       more than one, opening the FIRST one silently hid the rest; the
       owner had no way back to a second or third list except editing the
       URL by hand. This shows all of them first and lets the owner pick. */
    var pageListsCache = null;   // the last list of lists the switcher had, for the back button

    function pageListRowHTML(list) {
      return '<button type="button" class="cart-panel-list-row" data-circle-page-open-list="' + list.id + '">' +
        '<span class="cart-panel-list-thumb">' +
          (list.cover_image ? '<img src="' + esc(withWidth(list.cover_image, 96)) + '" alt="" loading="lazy">' : '') +
        '</span>' +
        '<span class="cart-panel-list-text">' +
          '<span class="cart-panel-list-name">' + esc(list.name) + '</span>' +
          '<span class="cart-panel-list-meta">' + esc(listMeta(list)) + '</span>' +
        '</span>' +
        '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.75 3.75L12 9L6.75 14.25" stroke="#A8A296" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>';
    }

    function showPageListSwitcher(lists) {
      pageListsCache = lists;
      pagePanel.removeAttribute('data-list-id');
      var titleEl = pagePanel.querySelector('.cart-panel-title');
      if (titleEl) titleEl.textContent = 'Your lists';
      var moreBtn = pagePanel.querySelector('[data-circle-list-settings]');
      if (moreBtn) moreBtn.hidden = true;
      var body = pagePanel.querySelector('.cart-panel-body');
      var footer = pagePanel.querySelector('.cart-panel-footer');
      if (body) body.innerHTML = '<div class="cart-panel-lists cart-panel-lists--page">' + lists.map(pageListRowHTML).join('') + '</div>';
      if (footer) footer.innerHTML = '';
    }

    function loadPageLists() {
      var body = pagePanel.querySelector('.cart-panel-body');
      if (body) body.innerHTML = SPINNER_HTML;
      return Api.call(function () { return Api.api('/lists'); })
        .then(function (d) {
          var lists = d.lists || [];
          if (!lists.length) {
            pageListsCache = [];
            var empty = pagePanel.querySelector('.cart-panel-body');
            if (empty) empty.innerHTML = '<p class="circle-mylist-empty">No lists yet. Add something from your cart to start one.</p>';
            return;
          }
          if (lists.length === 1) { pageListsCache = lists; return openList(lists[0].id, pagePanel, null); }
          showPageListSwitcher(lists);
        })
        .catch(function () {});
    }

    /* ---------------- Circle-vote page: what you've voted on ----------------
       A voter has no lists of their own — the owner's "your lists" default
       above has nothing to show them. This is that page's own default
       instead (data-circle-default-view="voted", set by page.circle-
       vote.json): every list the voter has ever given a take on, so a
       bare /pages/circle-vote isn't a dead end once you already know the
       page exists. Same switcher shape as the owner's, just built from
       GET /circle/voted instead of GET /circle/lists. */
    var pageVotedCache = null;

    // GET /voted never returns a list that's since been deleted (the
    // backend filters those out entirely) — nothing to check for here.
    function votedMeta(v) {
      var n = Number(v.item_count) || 0;
      var base = n + ' item' + (n === 1 ? '' : 's');
      if (v.share_status !== 'active') return base + ' · link closed';
      if (daysLeft(v.expires_at) === 0) return base + ' · link expired';
      return base;
    }

    function votedRowHTML(v) {
      return '<button type="button" class="cart-panel-list-row" data-circle-open-voted="' + esc(v.token) + '">' +
        '<span class="cart-panel-list-thumb">' +
          (v.cover_image ? '<img src="' + esc(withWidth(v.cover_image, 96)) + '" alt="" loading="lazy">' : '') +
        '</span>' +
        '<span class="cart-panel-list-text">' +
          '<span class="cart-panel-list-name">' + esc(v.list_name || 'A list') + '</span>' +
          '<span class="cart-panel-list-meta">' + esc(v.owner_name || 'Someone') + '&rsquo;s list &middot; ' + esc(votedMeta(v)) + '</span>' +
        '</span>' +
        '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.75 3.75L12 9L6.75 14.25" stroke="#A8A296" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>';
    }

    function showVotedSwitcher(voted) {
      pageVotedCache = voted;
      pagePanel.removeAttribute('data-list-id');
      var titleEl = pagePanel.querySelector('.cart-panel-title');
      if (titleEl) titleEl.textContent = 'Your takes';
      var moreBtn = pagePanel.querySelector('[data-circle-list-settings]');
      if (moreBtn) moreBtn.hidden = true;   // none of this is the voter's to rename/delete
      var body = pagePanel.querySelector('.cart-panel-body');
      var footer = pagePanel.querySelector('.cart-panel-footer');
      if (body) body.innerHTML = '<div class="cart-panel-lists cart-panel-lists--page">' + voted.map(votedRowHTML).join('') + '</div>';
      if (footer) footer.innerHTML = '';
    }

    function loadVotedLists() {
      var body = pagePanel.querySelector('.cart-panel-body');
      if (body) body.innerHTML = SPINNER_HTML;
      return Api.call(function () { return Api.api('/voted'); })
        .then(function (d) {
          var voted = d.voted || [];
          if (!voted.length) {
            pageVotedCache = [];
            pagePanel.removeAttribute('data-list-id');
            var titleEl = pagePanel.querySelector('.cart-panel-title');
            if (titleEl) titleEl.textContent = 'Your takes';
            var empty = pagePanel.querySelector('.cart-panel-body');
            if (empty) empty.innerHTML = '<p class="circle-mylist-empty">No takes yet — vote on a friend&rsquo;s shared link and it&rsquo;ll show up here.</p>';
            return;
          }
          if (voted.length === 1) return openSharedList(voted[0].token, pagePanel);
          showVotedSwitcher(voted);
        })
        .catch(function () {});
    }

    if (pagePanel) {
      var pageDefaultView = pagePanel.getAttribute('data-circle-default-view') || 'own';

      meReady.then(function () {
        var params = new URLSearchParams(window.location.search);
        var token = params.get('t');
        var listParam = params.get('list');
        if (token) return openSharedList(token, pagePanel);
        if (listParam) return openList(listParam, pagePanel, null);
        return pageDefaultView === 'voted' ? loadVotedLists() : loadPageLists();
      });

      pagePanel.addEventListener('click', function (e) {
        var row = e.target.closest('[data-circle-page-open-list]');
        if (row) { openList(row.getAttribute('data-circle-page-open-list'), pagePanel, null); return; }
        var votedRow = e.target.closest('[data-circle-open-voted]');
        if (votedRow) openSharedList(votedRow.getAttribute('data-circle-open-voted'), pagePanel);
      });
    }

    /* ---------------- "Your lists" row + Add-to grid (cart panel) ---------------- */

    function daysLeft(expiresAt) {
      if (!expiresAt) return null;
      var ms = new Date(expiresAt).getTime() - Date.now();
      if (ms <= 0) return 0;
      return Math.max(1, Math.round(ms / 86400000));
    }

    // Every place a list is listed (cart panel, add-to grid, the Circle
    // page switcher) says the same thing about it — whether it's shared,
    // and once it is, how long the link has left. A share is 7 days by
    // default; without this, a list could sit "shared" for a week after
    // the link that made it so had already stopped working.
    function listMeta(list) {
      var n = Number(list.item_count) || 0;
      var base = n + ' item' + (n === 1 ? '' : 's');
      if (!list.active_share_id) return base + ' · private';
      if (list.share_status && list.share_status !== 'active') return base + ' · link closed';
      var left = daysLeft(list.expires_at);
      if (left === 0) return base + ' · link expired';
      if (left != null) return base + ' · shared · ' + left + ' day' + (left === 1 ? '' : 's') + ' left';
      return base + ' · shared';
    }

    function renderYourLists() {
      var container = document.getElementById('cartYourLists');
      if (!container) return Promise.resolve();
      var empty = '<p class="cart-panel-list-empty">No lists yet.</p>';

      // A guest has no lists — don't ask, and don't leave a 401 in the
      // console every time the cart panel refreshes.
      if (Api.isGuest()) { container.innerHTML = empty; return Promise.resolve(); }
      return Api.api('/lists').then(function (d) {
        var lists = d.lists || [];
        if (!lists.length) {
          container.innerHTML = empty;
          return;
        }
        container.innerHTML = lists.map(function (list) {
          return '<button type="button" class="cart-panel-list-row" data-circle-open-mylist="' + list.id + '">' +
            '<span class="cart-panel-list-thumb">' +
              (list.cover_image ? '<img src="' + esc(withWidth(list.cover_image, 96)) + '" alt="" loading="lazy">' : '') +
            '</span>' +
            '<span class="cart-panel-list-text">' +
              '<span class="cart-panel-list-name">' + esc(list.name) + '</span>' +
              '<span class="cart-panel-list-meta">' + esc(listMeta(list)) + '</span>' +
            '</span>' +
            '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6.75 3.75L12 9L6.75 14.25" stroke="#A8A296" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
          '</button>';
        }).join('');
      }).catch(function () {
        container.innerHTML = empty;   // signed out or offline — the cart still works
      });
    }

    document.addEventListener('vro:cart-panel-refreshed', renderYourLists);
    renderYourLists();

    /* ---------------- cart item selection ---------------- */

    var cartPanel = document.getElementById('cartPanel');
    var selected = {};   // cart line key -> { handle, title }

    function refreshSelectBar() {
      var keys = Object.keys(selected);
      var bars = document.querySelectorAll('[data-cart-select-bar]');
      for (var i = 0; i < bars.length; i++) {
        bars[i].hidden = keys.length === 0;
        if (!keys.length) continue;
        var countEl = bars[i].querySelector('[data-cart-select-count]');
        var namesEl = bars[i].querySelector('[data-cart-select-names]');
        if (countEl) countEl.textContent = keys.length + ' selected';
        if (namesEl) namesEl.textContent = keys.map(function (k) { return selected[k].title; }).join(', ');
      }
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-cart-panel-open]')) { selected = {}; refreshSelectBar(); }
    });

    document.addEventListener('click', function (e) {
      var radio = e.target.closest('[data-cart-select]');
      if (!radio) return;
      var row = radio.closest('.cart-panel-item');
      var key = radio.getAttribute('data-cart-select');

      var handle = radio.getAttribute('data-cart-select-handle') || '';
      var title = radio.getAttribute('data-cart-select-title') || '';
      if (!handle) {
        var imgEl = row && row.querySelector('.cart-panel-item-img');
        handle = imgEl ? (imgEl.getAttribute('href') || '').split('/products/')[1] : '';
        if (handle) handle = handle.split('?')[0];
      }
      if (!title) {
        var titleEl = row && row.querySelector('.cart-panel-item-title');
        title = titleEl ? titleEl.textContent.trim() : '';
      }

      var nowSelected = !radio.classList.contains('is-selected');
      radio.classList.toggle('is-selected', nowSelected);
      radio.setAttribute('aria-pressed', nowSelected ? 'true' : 'false');
      if (nowSelected) selected[key] = { handle: handle, title: title };
      else delete selected[key];
      refreshSelectBar();
    });

    /* ---------------- Add-to view ---------------- */

    var addToGrid = document.getElementById('cartAddToGrid');
    var addToEyebrow = document.getElementById('cartAddToEyebrow');

    function renderAddToGrid() {
      if (!addToGrid) return Promise.resolve();
      var n = Object.keys(selected).length;
      if (addToEyebrow) addToEyebrow.textContent = 'SAVE ' + n + ' ITEM' + (n === 1 ? '' : 'S') + ' TO A LIST';

      /* Plain api(), not call(): opening this view is browsing, not an
         action worth throwing a login popup at someone for. A guest gets
         a tile that says so and starts the login only if they tap it. */
      if (Api.isGuest()) {
        addToGrid.innerHTML =
          '<button type="button" class="circle-addto-card circle-addto-card--signin" data-circle-signin>' +
            '<span class="circle-addto-name">Sign in</span>' +
            '<span class="circle-addto-meta">to save these to a list</span>' +
          '</button>';
        return Promise.resolve();
      }
      addToGrid.innerHTML = SPINNER_HTML;
      return Api.api('/lists').then(function (d) {
        var lists = d.lists || [];
        // The footer's "Create a list" button is always there below this
        // grid — this just says plainly that it's the only option right
        // now, instead of leaving the grid a blank gap.
        if (!lists.length) {
          addToGrid.innerHTML = '<p class="circle-addto-empty">No list created yet — start one below.</p>';
          return;
        }

        addToGrid.innerHTML = lists.map(function (list) {
          return '<button type="button" class="circle-addto-card" data-circle-addto-target="' + list.id + '">' +
            '<span class="circle-addto-thumb">' +
              (list.cover_image ? '<img src="' + esc(withWidth(list.cover_image, 240)) + '" alt="" loading="lazy">' : '') +
            '</span>' +
            '<span class="circle-addto-name">' + esc(list.name) + '</span>' +
            '<span class="circle-addto-meta">' + esc(listMeta(list)) + '</span>' +
          '</button>';
        }).join('');
      }).catch(function (err) {
        addToGrid.innerHTML = err.status === 401
          ? '<button type="button" class="circle-addto-card circle-addto-card--signin" data-circle-signin>' +
              '<span class="circle-addto-name">Sign in</span>' +
              '<span class="circle-addto-meta">to save these to a list</span>' +
            '</button>'
          : '';
      });
    }

    function openAddToView() {
      if (!cartPanel) return;
      if (!cartPanel.classList.contains('is-open') && window.VroCartPanel && window.VroCartPanel.open) {
        window.VroCartPanel.open();
      }
      cartPanel.classList.add('is-addto-mode');

      /* Required order: logged in (the caller already checked) → name, if
         we don't have one yet → THEN the grid of lists / create-a-list.
         Asking afterward meant the name prompt landed in the middle of
         "create a list", which is one screen too many at the wrong time. */
      ensureName().then(renderAddToGrid);
    }

    function closeAddToView() {
      if (cartPanel) cartPanel.classList.remove('is-addto-mode');
    }

    /* Turns the selected cart lines into real list items. Product data is
       read live from Shopify so the stored snapshot is accurate at the
       moment of adding (title/price/image), which is what the list shows
       if the product later disappears. */
    function addSelectedItemsToList(listId) {
      var keys = Object.keys(selected);
      if (!keys.length) return Promise.resolve(0);

      return Promise.all(keys.map(function (k) {
        var handle = selected[k].handle;
        if (!handle) return null;
        return fetchProduct(handle).then(function (p) {
          if (!p) return null;
          var variant = (p.variants || []).find(function (v) { return v && v.available; }) || (p.variants || [])[0];
          return Api.call(function (extra) {
            return Api.api('/lists/' + listId + '/items', {
              method: 'POST',
              body: Object.assign({
                product_gid: 'gid://shopify/Product/' + p.id,
                variant_gid: variant ? 'gid://shopify/ProductVariant/' + variant.id : undefined,
                snapshot: {
                  title: p.title,
                  handle: p.handle,
                  image: p.featured_image || (p.images && p.images[0]) || undefined,
                  price: p.price,
                  compare_at: p.compare_at_price || undefined,
                  currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || undefined,
                  vendor: p.vendor || undefined,
                  variant: variant && variant.title !== 'Default Title' ? variant.title : undefined
                }
              }, extra)
            });
          }).catch(function (err) {
            if (err.code === 'list_full' || err.code === 'too_many_lists') throw err;
            return null;   // one bad item shouldn't sink the whole batch
          });
        });
      })).then(function (results) {
        return results.filter(function (r) { return r && r.added !== false; }).length;
      });
    }

    /* ---------------- Create a list ---------------- */

    var createBackdrop = document.getElementById('circleCreateListBackdrop');
    var createModal = document.getElementById('circleCreateListModal');
    var createInput = document.getElementById('circleCreateListInput');
    var createCount = document.getElementById('circleCreateListCount');
    var createForm = document.getElementById('circleCreateListForm');

    function openCreateListModal() {
      if (!createBackdrop || !createModal) return;
      createBackdrop.hidden = false;
      requestAnimationFrame(function () {
        createBackdrop.classList.add('is-visible');
        createModal.classList.add('is-open');
      });
      createModal.removeAttribute('inert');
      createModal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (createInput) {
        createInput.value = '';
        if (createCount) createCount.textContent = '0/50';
        setTimeout(function () { createInput.focus(); }, 300);
      }
    }

    function closeCreateListModal() {
      if (!createBackdrop || !createModal) return;
      createBackdrop.classList.remove('is-visible');
      createModal.classList.remove('is-open');
      createModal.setAttribute('inert', '');
      createModal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      setTimeout(function () { createBackdrop.hidden = true; }, 320);
    }

    if (createInput) {
      createInput.addEventListener('input', function () {
        if (createCount) createCount.textContent = createInput.value.length + '/50';
      });
    }

    if (createForm) {
      createForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var name = (createInput.value || '').trim();
        if (!name) { createInput.focus(); return; }

        var submitBtn = document.getElementById('circleCreateListSubmit');
        Api.busy(submitBtn, true);

        Api.call(function (extra) {
          return Api.api('/lists', { method: 'POST', body: Object.assign({ name: name }, extra) });
        })
          .then(function (made) {
            return addSelectedItemsToList(made.id).then(function () {
              closeCreateListModal();
              closeAddToView();
              if (window.VroCartPanel) window.VroCartPanel.close();
              selected = {};
              refreshSelectBar();
              renderYourLists();
              // First list is the moment a name starts to matter — it's
              // about to be shared with people who need to know whose it is.
              return ensureName().then(function () { return openList(made.id); });
            });
          })
          .catch(function (err) {
            // consent_declined and login_required are both the customer
            // being asked something and not having answered yet — the
            // prompt or the login sheet is on screen saying so.
            if (err.code === 'consent_declined' || err.code === 'login_required') return;
            toast(err.code === 'too_many_lists'
              ? 'You have hit the list limit. Delete one first.'
              : "Couldn't create that list. Try again.");
          })
          .then(function () { Api.busy(submitBtn, false); });
      });
    }

    /* ---------------- Ask your Circle ---------------- */

    var askBackdrop = document.getElementById('circleAskBackdrop');
    var askModal = document.getElementById('circleAskModal');
    var askFriends = document.getElementById('circleAskFriends');
    var askSelectedCount = document.getElementById('circleAskSelectedCount');
    var askLinkInput = document.getElementById('circleAskLinkInput');
    var askCopyBtn = document.getElementById('circleAskCopyBtn');
    var askWhatsappBtn = document.getElementById('circleAskWhatsappBtn');
    var askToggle = document.getElementById('circleAskToggle');
    var askSendBtn = document.getElementById('circleAskSendBtn');
    var askSelected = {};
    var askMembers = [];
    var askShare = null;      // the share created for this list, once sent

    function updateAskCount() {
      var n = Object.keys(askSelected).length;
      if (askSelectedCount) askSelectedCount.textContent = '· ' + n + ' selected';
      if (askSendBtn) {
        askSendBtn.textContent = n
          ? 'Send to ' + n + ' ' + (n === 1 ? 'person' : 'people')
          : 'Get a link';
      }
    }

    /* Real Circle members (people who have voted on your lists and been
       added), not a fixed directory. Someone with an empty Circle still
       gets the link — that is how their first friend arrives. */
    function renderAskFriends() {
      if (!askFriends) return;
      if (!askMembers.length) {
        askFriends.innerHTML = '<p class="circle-ask-empty">No one in your Circle yet — share the link and whoever gives a take can be added.</p>';
        updateAskCount();
        return;
      }
      askFriends.innerHTML = askMembers.map(function (m) {
        return '<button type="button" class="circle-ask-friend' + (askSelected[m.id] ? ' is-active' : '') + '" ' +
          'data-circle-ask-friend="' + m.id + '">' +
          '<span class="circle-avatar">' + esc(initialsOf(m.display_name)) + '</span>' +
          esc(m.display_name || 'Someone') + '</button>';
      }).join('');
      updateAskCount();
    }

    function openAskCircleModal(listId) {
      if (!askBackdrop || !askModal || !listId) return;
      currentListId = listId;
      askShare = null;
      askSelected = {};
      if (askLinkInput) askLinkInput.value = 'Creating your link…';

      askBackdrop.hidden = false;
      requestAnimationFrame(function () {
        askBackdrop.classList.add('is-visible');
        askModal.classList.add('is-open');
      });
      askModal.removeAttribute('inert');
      askModal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      Api.api('/members')
        .then(function (d) {
          askMembers = (d && d.members) || [];
          askMembers.forEach(function (m) { askSelected[m.id] = true; });
          renderAskFriends();
        })
        .catch(function () { askMembers = []; renderAskFriends(); });

      // The link is created up front so it can be copied or previewed
      // without having to "send" first — the share is the link.
      createShare(listId);
    }

    function createShare(listId) {
      var memberIds = Object.keys(askSelected).map(Number);
      return Api.call(function (extra) {
        // No expires_in_days sent — the backend applies whatever the
        // merchant has set in Circle Settings (Admin → Circle → default
        // link expiry), so that setting actually controls something
        // instead of being overridden by a number hardcoded here.
        return Api.api('/lists/' + listId + '/share', {
          method: 'POST',
          body: Object.assign({ mode: 'vote', member_ids: memberIds }, extra)
        });
      }).then(function (share) {
        askShare = share;
        if (askLinkInput) askLinkInput.value = share.url;
        return share;
      }).catch(function (err) {
        if (askLinkInput) askLinkInput.value = '';
        if (err.code !== 'consent_declined' && err.code !== 'login_required') toast("Couldn't create the link. Try again.");
        throw err;
      });
    }

    // Exposed for the profile page's "Share again", which has a list id
    // but none of this panel's DOM to read it from.
    window.VroCircleAsk = { open: openAskCircleModal };

    function closeAskCircleModal() {
      if (!askBackdrop || !askModal) return;
      askBackdrop.classList.remove('is-visible');
      askModal.classList.remove('is-open');
      askModal.setAttribute('inert', '');
      askModal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      setTimeout(function () { askBackdrop.hidden = true; }, 320);
    }

    if (askFriends) {
      askFriends.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-circle-ask-friend]');
        if (!btn) return;
        var id = btn.getAttribute('data-circle-ask-friend');
        if (askSelected[id]) delete askSelected[id]; else askSelected[id] = true;
        btn.classList.toggle('is-active', !!askSelected[id]);
        updateAskCount();
      });
    }

    if (askCopyBtn) {
      askCopyBtn.addEventListener('click', function () {
        if (!askLinkInput || !askLinkInput.value) return;
        var done = function () { toast('Link copied'); };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(askLinkInput.value).then(done, done);
        } else { done(); }
      });
    }

    if (askWhatsappBtn) {
      askWhatsappBtn.addEventListener('click', function () {
        if (!askLinkInput || !askLinkInput.value) return;
        var text = encodeURIComponent('What do you think of these? ' + askLinkInput.value);
        window.open('https://wa.me/?text=' + text, '_blank', 'noopener');
      });
    }

    if (askToggle) {
      askToggle.addEventListener('click', function () {
        var on = !askToggle.classList.contains('is-on');
        askToggle.classList.toggle('is-on', on);
        askToggle.setAttribute('aria-checked', String(on));
      });
    }

    if (askSendBtn) {
      askSendBtn.addEventListener('click', function () {
        Api.busy(askSendBtn, true);
        var go = askShare ? createShare(currentListId) : createShare(currentListId);
        go.then(function () {
          toast(Object.keys(askSelected).length ? 'Sent to your Circle' : 'Link ready to share');
          closeAskCircleModal();
          renderYourLists();
          return openList(currentListId);
        }).catch(function () {})
          .then(function () { Api.busy(askSendBtn, false); });
      });
    }

    /* ---------------- Add people to your Circle ----------------
       "+ Add people" (profile) and "Add a new friend" (Ask modal) both
       land here. A voter is the only proof of a real relationship this
       app has — there's no directory, no email invite — so this shows
       everyone who has ever given a take on one of my lists and lets me
       add the ones I haven't already. Someone who's never voted has
       nothing to pick from; that case falls back to getting a link out
       instead, which is how a first voter ever appears here. */
    var votersEls = null;

    function buildVotersModal() {
      if (votersEls) return votersEls;
      var backdrop = document.createElement('div');
      backdrop.className = 'circle-modal-backdrop';
      backdrop.hidden = true;

      var modal = document.createElement('div');
      modal.className = 'circle-modal circle-modal--voters';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', 'Add people to your Circle');
      modal.innerHTML =
        '<div class="circle-modal-head"><h2 class="circle-modal-title">Add to your Circle</h2></div>' +
        '<p class="circle-modal-input-hint circle-voters-hint">People who&rsquo;ve given a take on one of your lists</p>' +
        '<div class="circle-voters-list" data-circle-voters-list></div>' +
        '<div class="circle-modal-actions circle-modal-actions--single">' +
          '<button type="button" class="circle-modal-cancel" data-circle-voters-close>Done</button>' +
        '</div>';

      document.body.appendChild(backdrop);
      document.body.appendChild(modal);
      votersEls = { backdrop: backdrop, modal: modal, list: modal.querySelector('[data-circle-voters-list]') };
      return votersEls;
    }

    function voterRowHTML(v) {
      return '<div class="circle-voter-row">' +
        '<span class="circle-avatar">' + esc(initialsOf(v.display_name)) + '</span>' +
        '<span class="circle-voter-name">' + esc(v.display_name || 'Someone') + '</span>' +
        (v.in_circle
          ? '<span class="circle-voter-added">In your Circle</span>'
          : '<button type="button" class="circle-voter-add-btn" data-circle-voter-add="' + v.id + '">Add</button>') +
      '</div>';
    }

    function renderVoters(voters) {
      var els = buildVotersModal();
      if (!voters.length) {
        els.list.innerHTML = '<p class="circle-mylist-empty">No one has given a take yet. Share a list to get your first one.</p>';
        return;
      }
      els.list.innerHTML = voters.map(voterRowHTML).join('');
    }

    function closeVotersModal() {
      if (!votersEls) return;
      votersEls.backdrop.classList.remove('is-visible');
      votersEls.modal.classList.remove('is-open');
      votersEls.modal.setAttribute('inert', '');
      votersEls.modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      setTimeout(function () { votersEls.backdrop.hidden = true; }, 320);
    }

    /**
     * Either opens the voters picker, or — nobody has ever voted on
     * anything of mine — falls straight to getting a link out, which is
     * how someone becomes a voter in the first place.
     */
    function openAddPeople() {
      Api.call(function () { return Api.api('/voters'); })
        .then(function (d) {
          var voters = (d && d.voters) || [];
          if (!voters.length) {
            return Api.api('/lists').then(function (dl) {
              var first = (dl.lists || [])[0];
              if (first) { openAskCircleModal(first.id); return; }
              toast('Create a list first');
            });
          }

          var els = buildVotersModal();
          els.list.innerHTML = SPINNER_HTML;
          els.backdrop.hidden = false;
          requestAnimationFrame(function () {
            els.backdrop.classList.add('is-visible');
            els.modal.classList.add('is-open');
          });
          els.modal.removeAttribute('inert');
          els.modal.setAttribute('aria-hidden', 'false');
          document.body.style.overflow = 'hidden';
          renderVoters(voters);
        })
        .catch(function (err) {
          if (err.code === 'consent_declined' || err.code === 'login_required') return;
          toast("Couldn't load your Circle.");
        });
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-circle-add-people]')) {
        if (Api.requireLogin()) openAddPeople();
        return;
      }
      if (e.target.closest('[data-circle-add-friend]')) {
        if (!Api.requireLogin()) return;
        closeAskCircleModal();
        setTimeout(openAddPeople, 240);
        return;
      }
      if (e.target.closest('[data-circle-voters-close]') || (votersEls && e.target === votersEls.backdrop)) {
        closeVotersModal();
        return;
      }
      var addBtn = e.target.closest('[data-circle-voter-add]');
      if (addBtn) {
        var memberId = addBtn.getAttribute('data-circle-voter-add');
        Api.busy(addBtn, true);
        Api.api('/members', { method: 'POST', body: { member_id: Number(memberId) } })
          .then(function () {
            var row = addBtn.closest('.circle-voter-row');
            if (row) row.innerHTML = row.querySelector('.circle-avatar').outerHTML +
              row.querySelector('.circle-voter-name').outerHTML +
              '<span class="circle-voter-added">In your Circle</span>';
            document.dispatchEvent(new CustomEvent('vro:session-changed'));   // profile card re-reads /members
          })
          .catch(function () {
            Api.busy(addBtn, false);
            toast("Couldn't add them.");
          });
      }
    });

    /* ---------------- notes, notify toggle, item removal ---------------- */

    document.addEventListener('click', function (e) {
      var panel = e.target.closest('.circle-panel, .cart-panel');

      var openNoteBtn = e.target.closest('[data-circle-add-note]');
      if (openNoteBtn && panel) {
        var itemId = openNoteBtn.getAttribute('data-circle-add-note');
        var form = panel.querySelector('[data-circle-note-form="' + itemId + '"]');
        if (form) {
          form.classList.add('is-open');
          var ta = form.querySelector('textarea');
          if (ta) ta.focus();
        }
        return;
      }

      var cancelBtn = e.target.closest('.circle-note-form-cancel');
      if (cancelBtn) {
        var cancelForm = cancelBtn.closest('.circle-note-form');
        if (cancelForm) {
          cancelForm.classList.remove('is-open');
          var cta = cancelForm.querySelector('textarea');
          if (cta) cta.value = '';
        }
        return;
      }

      var postBtn = e.target.closest('.circle-note-form-post');
      if (postBtn && panel) {
        var postForm = postBtn.closest('.circle-note-form');
        if (!postForm) return;
        var textarea = postForm.querySelector('textarea');
        var text = textarea ? textarea.value.trim() : '';
        var noteItemId = postForm.getAttribute('data-circle-note-form');
        var listId = panel.getAttribute('data-list-id') || currentListId;
        /* Set on a panel showing someone else's list through a share link.
           The owner-only route would refuse that note; this one takes it. */
        var shareToken = panel.getAttribute('data-share-token');
        if (!text || !noteItemId || (!listId && !shareToken)) return;

        Api.busy(postBtn, true);
        Api.call(function (extra) {
          var payload = Object.assign({ item_id: Number(noteItemId), body: text }, extra);
          return shareToken
            ? Api.api('/s/' + encodeURIComponent(shareToken) + '/notes', { method: 'POST', body: payload })
            : Api.api('/lists/' + listId + '/notes', { method: 'POST', body: payload });
        })
          .then(function () {
            textarea.value = '';
            postForm.classList.remove('is-open');
            if (shareToken) return openSharedList(shareToken, panel);
            return openList(listId, panel, panel === myListPanel ? myListBackdrop : null);
          })
          .catch(function (err) {
            if (err.code !== 'consent_declined' && err.code !== 'login_required') toast("Couldn't post that note.");
          })
          .then(function () { Api.busy(postBtn, false); });
        return;
      }

      var editNoteBtn = e.target.closest('[data-circle-note-edit]');
      if (editNoteBtn) {
        var editNoteId = editNoteBtn.getAttribute('data-circle-note-edit');
        var editForm = editNoteBtn.parentElement.querySelector('[data-circle-note-edit-form="' + editNoteId + '"]');
        if (editForm) {
          editNoteBtn.hidden = true;
          editForm.classList.add('is-open');
          var editTa = editForm.querySelector('textarea');
          if (editTa) { editTa.focus(); editTa.setSelectionRange(editTa.value.length, editTa.value.length); }
        }
        return;
      }

      var cancelEditBtn = e.target.closest('.circle-note-edit-cancel');
      if (cancelEditBtn) {
        var cancelEditForm = cancelEditBtn.closest('.circle-note-edit-form');
        if (cancelEditForm) {
          cancelEditForm.classList.remove('is-open');
          var reshowBtn = cancelEditForm.parentElement.querySelector('[data-circle-note-edit]');
          if (reshowBtn) reshowBtn.hidden = false;
        }
        return;
      }

      var saveEditBtn = e.target.closest('[data-circle-note-save]');
      if (saveEditBtn && panel) {
        var saveNoteId = saveEditBtn.getAttribute('data-circle-note-save');
        var saveForm = saveEditBtn.closest('.circle-note-edit-form');
        var saveTa = saveForm && saveForm.querySelector('textarea');
        var newBody = saveTa ? saveTa.value.trim() : '';
        if (!newBody) return;

        var saveListId = panel.getAttribute('data-list-id') || currentListId;
        var saveShareToken = panel.getAttribute('data-share-token');

        Api.busy(saveEditBtn, true);
        Api.api('/notes/' + saveNoteId, { method: 'PATCH', body: { body: newBody } })
          .then(function () {
            if (saveShareToken) return openSharedList(saveShareToken, panel);
            return openList(saveListId, panel, panel === myListPanel ? myListBackdrop : null);
          })
          .catch(function (err) {
            toast(err.code === 'already_edited' ? 'That note has already been edited once.' : "Couldn't save that.");
          })
          .then(function () { Api.busy(saveEditBtn, false); });
        return;
      }

      var removeBtn = e.target.closest('[data-circle-remove-item]');
      if (removeBtn && panel) {
        var removeItemId = removeBtn.getAttribute('data-circle-remove-item');
        var removeListId = panel.getAttribute('data-list-id') || currentListId;
        if (!removeListId) return;
        Api.api('/lists/' + removeListId + '/items/' + removeItemId, { method: 'DELETE' })
          .then(function () {
            renderYourLists();
            return openList(removeListId, panel, panel === myListPanel ? myListBackdrop : null);
          })
          .catch(function () { toast("Couldn't remove that."); });
        return;
      }

      var toggle = e.target.closest('[data-circle-notify-toggle]');
      if (toggle && panel) {
        var on = !toggle.classList.contains('is-on');
        var toggleListId = panel.getAttribute('data-list-id') || currentListId;
        toggle.classList.toggle('is-on', on);
        toggle.setAttribute('aria-checked', String(on));
        if (toggleListId) {
          Api.api('/lists/' + toggleListId, { method: 'PATCH', body: { notify_on_vote: on } })
            .catch(function () {
              toggle.classList.toggle('is-on', !on);
              toggle.setAttribute('aria-checked', String(!on));
              toast("Couldn't save that.");
            });
        }
      }
    });

    /* ---------------- add to bag (real Shopify Cart AJAX) ---------------- */

    document.addEventListener('click', function (e) {
      /* Only the Circle's own "Add" (.circle-sku-add). This listener sits
         on the document, so a bare [data-add-variant] also caught every
         product card's (+) — which custom.js already adds to the bag. One
         tap then added the item twice, and Api.busy() put the Circle's
         spinner on the (+) and switched it to position:relative, dropping
         it to the bottom-left of the card. The chat dock's add buttons
         carry the same attribute and have their own handler too. */
      var btn = e.target.closest('.circle-sku-add[data-add-variant]');
      if (!btn || btn.disabled) return;
      e.preventDefault();

      var variantId = parseInt(btn.getAttribute('data-add-variant'), 10);
      if (!variantId) { toast('This item is unavailable'); return; }

      Api.busy(btn, true);
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then(function (res) {
          return res.text().then(function (text) {
            var data = {};
            try { data = text ? JSON.parse(text) : {}; } catch (err) { /* non-JSON */ }
            if (!res.ok) throw new Error(data.description || data.message || 'Unable to add this item');
            return data;
          });
        })
        .then(function () {
          toast('Added to bag');
          return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
        })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (cart) {
          if (cart && typeof window.updateCartCount === 'function') window.updateCartCount(cart.item_count);
          document.dispatchEvent(new CustomEvent('vro:cart-updated', { detail: { cart: cart } }));
          document.dispatchEvent(new CustomEvent('vro:cart-panel-refreshed'));
        })
        .catch(function (err) {
          var msg = String((err && err.message) || '').toLowerCase();
          toast(/sold out|inventory|available/.test(msg) ? 'This item is sold out' : 'Could not add — try again');
        })
        .then(function () { Api.busy(btn, false); });
    });

    /* ---------------- global wiring ---------------- */

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-circle-panel-open]')) {
        e.preventDefault();
        if (window.VroCirclePanel) window.VroCirclePanel.open();
        return;
      }
      if (e.target.closest('[data-circle-panel-close]') || e.target === backdrop) {
        if (globalPanel) hidePanel(globalPanel, backdrop);
        return;
      }
      if (e.target.closest('[data-circle-mylist-close]') || e.target === myListBackdrop) {
        if (myListPanel) hidePanel(myListPanel, myListBackdrop);
        return;
      }

      var openMyList = e.target.closest('[data-circle-open-mylist]');
      if (openMyList) {
        if (Api.requireLogin()) openList(openMyList.getAttribute('data-circle-open-mylist'));
        return;
      }

      if (e.target.closest('[data-circle-signin]')) {
        // Opens the theme's login sheet. Signing in navigates away, so
        // there's nothing to do here afterwards.
        Api.login();
        return;
      }

      if (e.target.closest('[data-circle-addto-open]')) {
        if (Api.requireLogin()) openAddToView();
        return;
      }
      if (e.target.closest('[data-circle-addto-close]')) { closeAddToView(); return; }

      var addToCard = e.target.closest('[data-circle-addto-target]');
      if (addToCard) {
        var targetId = addToCard.getAttribute('data-circle-addto-target');
        addSelectedItemsToList(targetId)
          .then(function (added) {
            toast(added ? 'Added to your list' : 'Already in that list');
            closeAddToView();
            if (window.VroCartPanel) window.VroCartPanel.close();
            selected = {};
            refreshSelectBar();
            renderYourLists();
          })
          .catch(function (err) {
            if (err.code === 'consent_declined' || err.code === 'login_required') return;
            toast(err.code === 'list_full' ? 'That list is full.' : "Couldn't add those.");
          });
        return;
      }

      // Login → name (if we don't have one yet) → THEN the form. A guest
      // naming a list only to be sent to a login — and to lose the name on
      // the way — is the long way round to the same place; asking for a
      // name only after the list already exists is one screen too late for
      // the same reason. ensureName() resolves immediately when a name is
      // already on file, so this is a no-op the second time around.
      if (e.target.closest('[data-circle-create-list-open]')) {
        if (Api.requireLogin()) ensureName().then(openCreateListModal);
        return;
      }
      if (e.target.closest('[data-circle-createlist-close]') || e.target === createBackdrop) { closeCreateListModal(); return; }

      /* ---- the 3-dots, and the three things behind it ---- */
      if (e.target.closest('[data-circle-list-settings]')) {
        if (!Api.requireLogin()) return;
        var setPanel = e.target.closest('.circle-panel, .cart-panel');
        var setListId = (setPanel && setPanel.getAttribute('data-list-id')) || currentListId;
        if (setListId) openListSettings(setListId);
        return;
      }
      if (e.target.closest('[data-circle-settings-close]') ||
          (settingsEls && e.target === settingsEls.backdrop)) {
        closeListSettings();
        return;
      }
      if (e.target.closest('[data-circle-settings-ask]')) {
        var sAskId = settingsListId;
        closeListSettings();
        if (sAskId) openAskCircleModal(sAskId);
        return;
      }
      if (e.target.closest('[data-circle-settings-rename]')) {
        var sRenameId = settingsListId;
        closeListSettings();
        // Waits for the sheet to finish leaving before the next one arrives.
        if (sRenameId) setTimeout(function () { renameList(sRenameId); }, 240);
        return;
      }
      if (e.target.closest('[data-circle-settings-delete]')) {
        var sDeleteId = settingsListId;
        closeListSettings();
        if (sDeleteId) setTimeout(function () { deleteList(sDeleteId); }, 240);
        return;
      }

      if (e.target.closest('[data-circle-mylist-ask]')) {
        if (!Api.requireLogin()) return;
        var askPanel = e.target.closest('.circle-panel, .cart-panel');
        var askListId = (askPanel && askPanel.getAttribute('data-list-id')) || currentListId;
        if (askListId) openAskCircleModal(askListId);
        return;
      }

      // The cart panel's "Ask about ..." card — no list of its own, so it
      // asks about the most recent one.
      if (e.target.closest('[data-circle-ask-primary-list]')) {
        if (!Api.requireLogin()) return;
        Api.call(function () { return Api.api('/lists'); })
          .then(function (d) {
            var first = (d.lists || [])[0];
            if (first) openAskCircleModal(first.id);
            else toast('Create a list first');
          })
          .catch(function () {});
        return;
      }

      if (e.target.closest('[data-circle-ask-close]') || e.target === askBackdrop) { closeAskCircleModal(); return; }

      if (e.target.closest('[data-circle-invite]')) {
        if (!Api.requireLogin()) return;
        var invitePanel = e.target.closest('.circle-panel, .cart-panel');
        var inviteListId = (invitePanel && invitePanel.getAttribute('data-list-id')) || currentListId;
        if (inviteListId) openAskCircleModal(inviteListId);
        return;
      }

      var backBtn = e.target.closest('[data-circle-back]');
      if (backBtn) {
        // Viewing one of several lists on the Circle page: back means back
        // to the list of lists, not off the page entirely — whichever
        // switcher got you here, your own lists or the voted-on ones.
        if (pagePanel && pageListsCache && pageListsCache.length > 1 && pagePanel.getAttribute('data-list-id')) {
          showPageListSwitcher(pageListsCache);
          return;
        }
        if (pagePanel && pageVotedCache && pageVotedCache.length > 1 && pagePanel.getAttribute('data-list-id')) {
          showVotedSwitcher(pageVotedCache);
          return;
        }
        if (window.history.length > 1) window.history.back();
        else window.location.href = '/';
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (createModal && createModal.classList.contains('is-open')) closeCreateListModal();
      else if (askModal && askModal.classList.contains('is-open')) closeAskCircleModal();
      else if (myListPanel && myListPanel.classList.contains('is-open')) hidePanel(myListPanel, myListBackdrop);
      else if (globalPanel && globalPanel.classList.contains('is-open')) hidePanel(globalPanel, backdrop);
      else if (cartPanel && cartPanel.classList.contains('is-addto-mode')) closeAddToView();
    });
  });
})();
